import { motion, useScroll, useTransform } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import placeholderImg from '@/assets/placeholder-project.jpg';
import positiveTechImg from '@/assets/cases/positive-technologies.jpg';
import megafonImg from '@/assets/cases/megafon-pre5g.jpg';
import futureGamesImg from '@/assets/cases/future-games.jpg';

const featuredPlaceholders = [
  { titleRu: 'Positive Technologies', titleEn: 'Positive Technologies', descRu: 'Зрелищное и технологичное шоу, демонстрирующее инновационность нового решения.', descEn: 'A spectacular and technological show demonstrating the innovation of a new solution.', image: positiveTechImg },
  { titleRu: 'Megafon - Pre-5G', titleEn: 'Megafon - Pre-5G', descRu: 'Рекламный ролик с Александром Петровым, приуроченный к открытию доступа к сети 5G.', descEn: 'A commercial featuring Alexander Petrov, dedicated to the launch of 5G network access.', image: megafonImg },
  { titleRu: 'Future Games', titleEn: 'Future Games', descRu: 'Международный мультиспортивный фиджитал-турнир', descEn: 'International multisport phygital tournament', image: futureGamesImg },
];

const basePlaceholders = [
  { titleRu: 'Брендинг кофейни', titleEn: 'Coffee shop branding', descRu: 'Разработка фирменного стиля, логотипа и упаковки для сети кофеен.', descEn: 'Brand identity, logo and packaging design for a coffee shop chain.' },
  { titleRu: 'Мобильное приложение', titleEn: 'Mobile app', descRu: 'UI/UX дизайн мобильного приложения для управления финансами.', descEn: 'UI/UX design for a personal finance management mobile app.' },
  { titleRu: 'Корпоративный сайт', titleEn: 'Corporate website', descRu: 'Проектирование и дизайн корпоративного сайта для IT-компании.', descEn: 'Design and development of a corporate website for an IT company.' },
  { titleRu: 'Рекламная кампания', titleEn: 'Ad campaign', descRu: 'Визуальная концепция и креативы для digital-рекламной кампании.', descEn: 'Visual concept and creatives for a digital advertising campaign.' },
  { titleRu: 'Интернет-магазин', titleEn: 'E-commerce store', descRu: 'Дизайн интернет-магазина одежды с адаптивной версткой.', descEn: 'Responsive e-commerce store design for a fashion brand.' },
  { titleRu: 'Айдентика стартапа', titleEn: 'Startup identity', descRu: 'Полный цикл разработки визуальной идентичности для технологического стартапа.', descEn: 'Full-cycle visual identity design for a tech startup.' },
  { titleRu: 'Дизайн упаковки', titleEn: 'Packaging design', descRu: 'Концепция и дизайн упаковки для линейки органических продуктов.', descEn: 'Packaging concept and design for an organic product line.' },
  { titleRu: 'Лендинг продукта', titleEn: 'Product landing page', descRu: 'Высококонверсионный лендинг для запуска нового SaaS-продукта.', descEn: 'High-converting landing page for a new SaaS product launch.' },
  { titleRu: 'Навигационная система', titleEn: 'Wayfinding system', descRu: 'Проектирование навигации и указателей для торгового центра.', descEn: 'Wayfinding and signage design for a shopping mall.' },
  { titleRu: 'Дашборд аналитики', titleEn: 'Analytics dashboard', descRu: 'Проектирование интерфейса дашборда для бизнес-аналитики.', descEn: 'UI design for a business analytics dashboard.' },
  { titleRu: 'Ребрендинг ресторана', titleEn: 'Restaurant rebrand', descRu: 'Обновление визуальной идентичности и меню для ресторана.', descEn: 'Visual identity refresh and menu redesign for a restaurant.' },
];

const remainingPlaceholders = Array.from({ length: 63 }, (_, i) => basePlaceholders[i % basePlaceholders.length]);

const allPlaceholders = [
  ...featuredPlaceholders.map(p => ({ ...p, image: p.image })),
  ...remainingPlaceholders.map(p => ({ ...p, image: placeholderImg })),
];

const PlaceholderCard = ({ item, index }: { item: typeof allPlaceholders[0]; index: number }) => {
  const { lang } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);

  return (
    <Link to={`/case/placeholder-${index}`}>
    <div
      ref={ref}
      className="block relative w-full aspect-[16/9] rounded-2xl overflow-hidden group cursor-pointer"
    >
      <img src={item.image} alt="Project placeholder" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-background/40 group-hover:bg-background/80 transition-colors duration-500" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
        <h3 className="text-base md:text-xl group-hover:text-sm group-hover:md:text-base font-display font-bold mb-0 transition-all duration-700 ease-out group-hover:-translate-y-2 group-hover:mb-1 group-hover:text-primary">
          {lang === 'ru' ? item.titleRu : item.titleEn}
        </h3>
        <p className="text-muted-foreground text-xs md:text-sm leading-relaxed max-w-xl max-h-0 opacity-0 overflow-hidden transition-all duration-700 ease-out group-hover:max-h-40 group-hover:opacity-100">
          {lang === 'ru' ? item.descRu : item.descEn}
        </p>
      </div>
    </div>
    </Link>
  );
};

const Portfolio = () => {
  const { lang, t } = useI18n();

  const { data: cases, isLoading } = useQuery({
    queryKey: ['cases-all'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cases')
        .select('*')
        .eq('is_published', true)
        .order('sort_order');
      return data || [];
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="pt-28 md:pt-36 pb-24">
        <div className="container">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-16 text-center">
            {t('Все проекты', 'All projects')}
          </h1>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-secondary animate-pulse" />
              ))}
            </div>
          ) : cases && cases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cases.map((item) => (
                <div key={item.id}>
                  <Link
                    to={`/case/${item.slug}`}
                    className="group block relative aspect-[4/3] bg-secondary rounded-2xl overflow-hidden card-shadow"
                  >
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={lang === 'ru' ? item.title_ru : item.title_en}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary">
                        <span className="text-4xl text-muted-foreground/20 font-display font-bold">
                          {(lang === 'ru' ? item.title_ru : item.title_en).charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6 md:p-8">
                      <div className="flex-1">
                        <p className="text-primary text-xs font-display font-medium tracking-wider uppercase mb-2">
                          {lang === 'ru' ? item.category_ru : item.category_en}
                        </p>
                        <h3 className="text-xl md:text-2xl font-display font-bold text-foreground">
                          {lang === 'ru' ? item.title_ru : item.title_en}
                        </h3>
                      </div>
                      <ArrowUpRight className="text-primary" size={24} />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allPlaceholders.map((item, i) => (
                <PlaceholderCard key={i} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;
